import React from "react";

/**
 * One card system for the Hub — and, since the v3 rebuild, the same design system both
 * partner portals use (`styles/portal-sys.css`, generated from `_mockups/v3/sys.css`).
 * Every block here now renders a v3 class rather than a hand-rolled Tailwind shell, so a
 * Hub page and a portal page are drawn by the same stylesheet.
 *
 * Status colour is the traffic-light scale the owner signed off in Oct 2026 (see
 * `lib/stages.js`): **amber** = waiting on somebody · **brand blue** = being read / act now ·
 * **green** = money · **red** = late or dead · **slate** = nothing to say. Red is still never
 * used for an ordinary control — only for a status that is genuinely dead, or an irreversible
 * action (delete, purge).
 */
const EDGE = {
  plain: "var(--line)", wait: "var(--amber)", act: "var(--accent)",
  dead: "var(--mut)", good: "var(--green)", brand: "var(--accent)",
};
const ICON = {
  plain: "text-[#0055FF]", wait: "text-[#B07A00]", act: "text-[#0055FF]",
  dead: "text-[#64748B]", good: "text-[#0B6B4F]", brand: "text-[#0055FF]",
};

export const Card = ({ label, Icon, right, tone = "plain", children, testId, className = "" }) => (
  <div data-testid={testId} className={`card ${className}`}
       style={{ position: "relative", overflow: "hidden", padding: 24, minWidth: 0 }}>
    <i style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: EDGE[tone] || EDGE.plain }} />
    {(label || right) && (
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 16, alignItems: "flex-start" }}>
        <div className="rail-t" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {Icon && <Icon className={`size-3 shrink-0 ${ICON[tone] || ICON.plain}`} />}
          <span className="sm:truncate">{label}</span>
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const Section = ({ title, hint, right, children, testId }) => (
  <section className="sec" data-testid={testId}>
    <div className="sec-head" style={{ borderBottom: "1px solid var(--line)", paddingBottom: 12 }}>
      <div style={{ minWidth: 0 }}>
        <h2 className="s">{title}</h2>
        {hint && <p className="sec-sub">{hint}</p>}
      </div>
      {right}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

/** PageHead — the one page title treatment across the Hub. Mirrors the v3 crm/kit head so the
 *  actions sit to the right of the title on desktop and drop below with a proper gap on mobile. */
export const PageHead = ({ eyebrow, title, sub, right, testId }) => (
  <div className="head" data-testid={testId}>
    <div className="head-row">
      <div style={{ minWidth: 0 }}>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1 className="t">{title}</h1>
      </div>
      {right && <div className="head-actions">{right}</div>}
    </div>
    {sub && <p className="lede">{sub}</p>}
  </div>
);

export const Quiet = ({ children, testId }) => (
  <p className="sec-sub" style={{ marginTop: 14 }} data-testid={testId}>{children}</p>
);

export const Note = ({ children }) => (
  <p style={{ marginTop: 14, borderTop: "1px solid var(--line-soft)", paddingTop: 12,
              fontSize: 11.5, lineHeight: 1.6, color: "var(--mut-2)" }}>{children}</p>
);

/* The v3 status chip, keyed by the Hub's older tone names so no page has to change. */
const PILL_CLASS = {
  slate: "st-neutral", wait: "st-sent", act: "st-rev", dead: "st-dec",
  good: "st-off", brand: "st-rev", ink: "st-new", quiet: "st-quiet", green: "st-fund",
};

export const Pill = ({ tone = "slate", children, testId, className = "" }) => (
  <span data-testid={testId} className={`st ${PILL_CLASS[tone] || PILL_CLASS.slate} ${className}`}>
    {children}
  </span>
);

/** Figure — the only way a number is displayed. */
export const Figure = ({ label, value, sub, tone = "slate", testId }) => {
  const tones = { slate: "var(--ink)", good: "var(--green)", wait: "#7A4800",
                  act: "#0A3DB8", dead: "var(--mut)", brand: "var(--accent)" };
  return (
    <div className="stat" style={{ padding: 0, background: "transparent", minWidth: 0 }} data-testid={testId}>
      <div className="k" title={label}>{label}</div>
      <div className="v" style={{ color: tones[tone] || tones.slate }}>{value ?? 0}</div>
      {sub && <div className="h">{sub}</div>}
    </div>
  );
};

/** RangeToggle — the 7d / 30d switch, identical everywhere it appears. */
export const RangeToggle = ({ options, value, onChange, testPrefix, suffix = "d" }) => (
  <div className="tgl">
    {options.map((d) => (
      <button key={d} onClick={() => onChange(d)} className={value === d ? "on" : undefined}
              data-testid={`${testPrefix}-${d}${suffix}`}>
        {d === 0 ? "All" : `${d}${suffix}`}
      </button>
    ))}
  </div>
);

/* ─── Money ───────────────────────────────────────────────────────── */
export const gbp = (v, { compact = false, pence = false } = {}) => {
  const n = Number(v || 0);
  const neg = n < 0;
  const abs = Math.abs(n);
  let body;
  if (compact && abs >= 1_000_000) body = `${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}m`;
  else if (compact && abs >= 10_000) body = `${Math.round(abs / 1000)}k`;
  else body = abs.toLocaleString("en-GB", { minimumFractionDigits: pence ? 2 : 0, maximumFractionDigits: pence ? 2 : 0 });
  return `${neg ? "−" : ""}£${body}`;
};

const MONEY_TONES = {
  in: "text-[#0B6B4F]", out: "text-slate-500", net: "text-[#0B1220]",
  brand: "text-[#0055FF]", muted: "text-slate-400",
};

/** Money — tabular Archivo, never wrapped. The only way currency is printed in the Hub. */
export const Money = ({ value, tone = "net", size = "sm", compact = false, prefix, testId, className = "" }) => {
  const sizes = { xs: "text-[11.5px]", sm: "text-[13.5px]", md: "text-[15.5px]", lg: "text-[26px] leading-none", xl: "text-[34px] leading-none" };
  return (
    <span data-testid={testId}
          className={`money whitespace-nowrap ${sizes[size] || sizes.sm} ${MONEY_TONES[tone] || MONEY_TONES.net} ${className}`}>
      {prefix}{gbp(value, { compact })}
    </span>
  );
};

/** LiveDot — a quiet pulse that says the numbers on screen are current. */
export const LiveDot = ({ label = "Live", tone = "emerald", testId }) => {
  const c = tone === "emerald" ? "bg-[#0B6B4F]" : "bg-[#0055FF]";
  return (
    <span className="rail-t" style={{ marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 6 }} data-testid={testId}>
      <span className="relative flex size-1.5">
        <span className={`absolute inline-flex size-full animate-ping rounded-full ${c} opacity-60`} />
        <span className={`relative inline-flex size-1.5 rounded-full ${c}`} />
      </span>
      {label}
    </span>
  );
};

/** TabBar — segmented filter with counts, the mockups' chip row. */
export const TabBar = ({ items, value, onChange, testPrefix }) => (
  <div className="chips hub-scroll" role="tablist">
    {items.map((it) => (
      <button key={it.key} role="tab" aria-selected={value === it.key} onClick={() => onChange(it.key)}
              className={`chip${value === it.key ? " on" : ""}`} data-testid={`${testPrefix}-${it.key}`}>
        {it.dot && <i className="sq" style={{ background: it.dot }} />}
        {it.label}
        <span className="n">{it.count}</span>
      </button>
    ))}
  </div>
);

/** Loading — the shape of the rows that are coming, rather than a spinning ring. */
export const Loading = ({ rows = 3, testId = "hub-loading" }) => (
  <div className="space-y-3 p-5" data-testid={testId}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        <div className="skel h-9 w-9 shrink-0 rounded-[9px]" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="skel h-3" style={{ width: `${62 - i * 9}%` }} />
          <div className="skel h-2.5" style={{ width: `${40 - i * 6}%` }} />
        </div>
        <div className="skel h-3 w-16 shrink-0" />
      </div>
    ))}
  </div>
);
